import { useEffect } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMediaQuery } from "react-responsive";

import { QUERY_WIDTHS, changePhoneSchema } from "@utils/index";
import { ChangePhoneFormInterface } from "@interfaces/index";
import { useCommonStore, useUserStore } from "@stores/index";
import { useUserGetProfile, useUserChangePhone } from "@providers/index";
import { useNotify } from "@hooks/index";
import {
  Form,
  FormRow,
  FormInput,
  Button,
  MobileHeader,
} from "@components/index";

export const ChangePhoneForm = () => {
  const setCommonState = useCommonStore(({ setState }) => setState);
  const setUserState = useUserStore(({ setState }) => setState);
  const notify = useNotify();
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });

  const {
    isPending: isPendingGetProfile,
    isSuccess,
    data,
    isError,
    error,
  } = useUserGetProfile();

  const { mutate: mutateUserChangePhone, isPending: isPendingChangePhone } =
    useUserChangePhone(
      ({ phone }) => {
        setUserState({ phone });
        notify("success", "Your phone has been successfully updated.");
      },
      ({ message, fields }) => {
        Object.keys(fields || {}).forEach((key) =>
          setError(key as "phone", { message: fields![key] }),
        );
        notify("error", message);
      },
    );

  useEffect(() => {
    setCommonState({ isLoading: isPendingGetProfile || isPendingChangePhone });
  }, [isPendingGetProfile, isPendingChangePhone, setCommonState]);

  const { register, handleSubmit, formState, reset, clearErrors, setError } =
    useForm<ChangePhoneFormInterface>({
      mode: "onBlur",
      disabled: isPendingGetProfile || isPendingChangePhone,
      resolver: yupResolver(changePhoneSchema),
    });

  useEffect(() => {
    if (isSuccess && data) {
      reset({ ...data });
    }
  }, [isSuccess, data, reset]);

  useEffect(() => {
    if (isError && error) {
      notify("error", error.message);
    }
  }, [isError, error, notify]);

  const onSubmit: SubmitHandler<ChangePhoneFormInterface> = (data) =>
    mutateUserChangePhone({ ...data });

  return (
    <>
      <MobileHeader href="/profile" title="Change Phone" />
      <div className="px-4 pt-10 text-center text-black-54 md:px-24 md:pt-2 md:text-left">
        Your number can be used to deliver important notifications
      </div>
      <Form
        onSubmit={handleSubmit(onSubmit)}
        className="px-4 text-black-100 md:max-w-[632px] md:px-24"
      >
        <FormRow top={isMobile ? "medium" : "large"}>
          <FormInput
            {...register<"phone">("phone")}
            type="phone"
            label="Phone"
            placeholder="Phone *"
            error={formState.errors.phone?.message}
            onChange={() => clearErrors("phone")}
          />
        </FormRow>
        <FormRow isRow isBetween top="large">
          <Button type="submit" title="Save" className="w-52" />
          <Button
            title="Cancel"
            variant="text"
            isLink
            href="/profile"
            className="text-black-54"
          />
        </FormRow>
      </Form>
    </>
  );
};
